import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Save, X, Trash2, RefreshCw, Users, Search, Percent, Phone, Edit3,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_P = { name: "", full_name: "", nip: "", email: "", phone: "", device_user_id: "", commission_percentage: "", active: true };

const identityPayload = (p) => ({
  full_name: p.full_name?.trim() || null,
  nip: p.nip?.trim() || null,
  email: p.email?.trim() || null,
});

function IdentityCell({ r, ed, field, setEditing, placeholder, mono }) {
  return (
    <TableCell className={`text-[12px] text-zinc-700 ${mono ? "font-mono" : ""}`} data-testid={`personel-${field}-${r.id}`}>
      {ed ? (
        <Input value={ed[field] || ""}
          onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], [field]: e.target.value } }))}
          className={`h-8 rounded-sm text-[11px] min-w-[120px] ${mono ? "font-mono" : ""}`}
          placeholder={placeholder}
          data-testid={`edit-${field}-${r.id}`} />
      ) : (r[field] || <span className="text-zinc-400">—</span>)}
    </TableCell>
  );
}


/** v82 — Data Teknisi (Personel individu dengan % komisi).
 *  Beda dari halaman "Teknisi" yg mengelola ARMADA (kendaraan/tim GA-02 dll).
 *  Personel di sini adalah orang, di-assign ke armada per hari via Rekap Penagihan.
 *  Komisi = Jasa Teknisi armada × commission_percentage. */
export default function Personel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [newP, setNewP] = useState(EMPTY_P);
  const [editing, setEditing] = useState({}); // { id: {name, phone, commission_percentage, active} }

  async function load() {
    setLoading(true);
    try {
      setRows(await api.listPersonnel());
    } catch (e) {
      toast.error("Gagal muat: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return rows;
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(kw) ||
      (r.phone || "").toLowerCase().includes(kw) ||
      (r.full_name || "").toLowerCase().includes(kw) ||
      (r.nip || "").toLowerCase().includes(kw) ||
      (r.email || "").toLowerCase().includes(kw) ||
      (r.device_user_id || "").toLowerCase().includes(kw)
    );
  }, [rows, q]);

  const activeCount = rows.filter(r => r.active).length;

  function startEdit(row) {
    setEditing((e) => ({ ...e, [row.id]: {
      name: row.name, phone: row.phone || "",
      full_name: row.full_name || "", nip: row.nip || "", email: row.email || "",
      device_user_id: row.device_user_id || "",
      commission_percentage: row.commission_percentage, active: row.active,
    }}));
  }
  function cancelEdit(id) {
    setEditing((e) => { const c = { ...e }; delete c[id]; return c; });
  }
  async function saveEdit(id) {
    const p = editing[id];
    if (!p) return;
    const pct = Number(p.commission_percentage);
    if (!p.name?.trim()) { toast.error("Nama wajib"); return; }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Persentase komisi harus 0-100"); return;
    }
    try {
      const updated = await api.updatePersonnel(id, {
        name: p.name.trim(), phone: p.phone?.trim() || null,
        ...identityPayload(p),
        device_user_id: p.device_user_id?.trim() || null,
        commission_percentage: pct, active: !!p.active,
      });
      setRows((rs) => rs.map((r) => (r.id === id ? updated : r)));
      cancelEdit(id);
      toast.success(`${p.name} diperbarui`);
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function del(row) {
    if (!window.confirm(`Hapus ${row.name}?\n\nSemua assignment armada ke personel ini akan otomatis di-clear.`)) return;
    try {
      await api.deletePersonnel(row.id);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      toast.success(`${row.name} dihapus`);
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function addNew() {
    const pct = Number(newP.commission_percentage);
    if (!newP.name?.trim()) { toast.error("Nama wajib"); return; }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Persentase komisi harus 0-100"); return;
    }
    try {
      const created = await api.createPersonnel({
        name: newP.name.trim(),
        phone: newP.phone?.trim() || null,
        ...identityPayload(newP),
        device_user_id: newP.device_user_id?.trim() || null,
        commission_percentage: pct,
        active: !!newP.active,
      });
      setRows((rs) => [...rs, created].sort((a,b) => a.name.localeCompare(b.name)));
      setNewP(EMPTY_P);
      setAdding(false);
      toast.success(`${created.name} ditambahkan`);
    } catch (e) {
      toast.error("Gagal tambah: " + (e?.response?.data?.detail || e.message));
    }
  }

  return (
    <div className="space-y-4" data-testid="personel-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" />
            Data Karyawan (Teknisi & Staff)
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Master data personel individu + persentase komisi. Assign personel ke armada per hari di halaman <b>Rekap Penagihan</b>.
          </p>
        </div>
        <Button
          size="sm" onClick={() => setAdding(true)}
          className="h-9 rounded-sm gap-1.5 bg-emerald-700 hover:bg-emerald-800"
          data-testid="add-personel-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Tambah Teknisi
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border-2 border-emerald-700 bg-emerald-50 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Total Personel</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 font-mono" data-testid="total-personel">
            {rows.length}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{activeCount} aktif</div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Rata-rata Komisi</div>
          <div className="text-2xl font-bold text-zinc-800 mt-1 font-mono">
            {rows.length > 0
              ? (rows.reduce((a,b) => a + Number(b.commission_percentage||0), 0) / rows.length).toFixed(1)
              : "0.0"}%
          </div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-4 flex flex-col justify-center">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Cari</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nama, HP, atau ID Mesin…"
              className="pl-7 h-9 rounded-sm text-[12px]"
              data-testid="search-personel-input" />
          </div>
        </div>
      </div>

      {/* Add row form */}
      {adding && (
        <div className="border-2 border-emerald-400 bg-emerald-50/50 rounded-sm p-4" data-testid="add-personel-form">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-2">
            Personel Baru
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <Input value={newP.name}
              onChange={(e) => setNewP({ ...newP, name: e.target.value })}
              placeholder="Nama (mis. Hendro)"
              className="h-9 rounded-sm text-[12px]"
              autoFocus
              data-testid="new-personel-name-input" />
            <Input value={newP.full_name}
              onChange={(e) => setNewP({ ...newP, full_name: e.target.value })}
              placeholder="Nama lengkap"
              className="h-9 rounded-sm text-[12px]"
              data-testid="new-personel-fullname-input" />
            <Input value={newP.nip}
              onChange={(e) => setNewP({ ...newP, nip: e.target.value })}
              placeholder="NIP"
              className="h-9 rounded-sm text-[12px] font-mono"
              data-testid="new-personel-nip-input" />
            <Input type="email" value={newP.email}
              onChange={(e) => setNewP({ ...newP, email: e.target.value })}
              placeholder="Email (untuk slip gaji)"
              className="h-9 rounded-sm text-[12px]"
              data-testid="new-personel-email-input" />
            <Input value={newP.phone}
              onChange={(e) => setNewP({ ...newP, phone: e.target.value })}
              placeholder="Nomor HP (opsional)"
              className="h-9 rounded-sm text-[12px] font-mono"
              data-testid="new-personel-phone-input" />
            <Input value={newP.device_user_id}
              onChange={(e) => setNewP({ ...newP, device_user_id: e.target.value })}
              placeholder="ID Mesin Absensi"
              className="h-9 rounded-sm text-[12px] font-mono"
              data-testid="new-personel-device-uid-input" />
            <div className="relative">
              <Percent className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input type="number" min="0" max="100" step="0.5"
                value={newP.commission_percentage}
                onChange={(e) => setNewP({ ...newP, commission_percentage: e.target.value })}
                placeholder="Komisi %"
                className="h-9 rounded-sm text-[12px] font-mono pr-7"
                data-testid="new-personel-pct-input" />
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-700 px-2">
              <input type="checkbox" checked={newP.active}
                onChange={(e) => setNewP({ ...newP, active: e.target.checked })} />
              Aktif
            </label>
            <div className="flex gap-1">
              <Button size="sm" onClick={addNew} className="h-9 rounded-sm gap-1 bg-emerald-700 hover:bg-emerald-800 flex-1" data-testid="save-new-personel-btn">
                <Save className="w-3.5 h-3.5" /> Simpan
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewP(EMPTY_P); }}
                className="h-9 rounded-sm">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-zinc-50">
            <TableRow>
              <TableHead>Nama (Nick)</TableHead>
              <TableHead>Nama (Full)</TableHead>
              <TableHead>NIP</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-36">Nomor HP</TableHead>
              <TableHead className="w-32">ID Mesin Absensi</TableHead>
              <TableHead className="w-28 text-right">Komisi %</TableHead>
              <TableHead className="w-24 text-center">Status</TableHead>
              <TableHead className="w-32 text-center">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-zinc-400 py-6">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Memuat…
              </TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-zinc-400 py-6">
                {q ? "Tidak ada personel yang cocok" : "Belum ada personel. Klik + Tambah Teknisi."}
              </TableCell></TableRow>
            )}
            {!loading && filtered.map((r) => {
              const ed = editing[r.id];
              return (
                <TableRow key={r.id} data-testid={`personel-row-${r.id}`}>
                  <TableCell className="font-semibold text-[13px]">
                    {ed ? (
                      <Input value={ed.name}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], name: e.target.value } }))}
                        className="h-8 rounded-sm text-[12px]" autoFocus
                        data-testid={`edit-name-${r.id}`} />
                    ) : r.name}
                  </TableCell>
                  <IdentityCell r={r} ed={ed} field="full_name" setEditing={setEditing} placeholder="Nama lengkap" />
                  <IdentityCell r={r} ed={ed} field="nip" setEditing={setEditing} placeholder="NIP" mono />
                  <IdentityCell r={r} ed={ed} field="email" setEditing={setEditing} placeholder="Email" />
                  <TableCell className="text-[11px] font-mono text-zinc-600">
                    {ed ? (
                      <Input value={ed.phone || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], phone: e.target.value } }))}
                        className="h-8 rounded-sm text-[11px] font-mono"
                        placeholder="Opsional" />
                    ) : (r.phone || <span className="text-zinc-400">—</span>)}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono text-zinc-600">
                    {ed ? (
                      <Input value={ed.device_user_id || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], device_user_id: e.target.value } }))}
                        className="h-8 rounded-sm text-[11px] font-mono"
                        placeholder="ID Mesin"
                        data-testid={`edit-device-uid-${r.id}`} />
                    ) : (r.device_user_id ? (
                      <span className="px-1.5 py-0.5 rounded-sm bg-cyan-50 text-cyan-800 border border-cyan-200 text-[10px]">
                        {r.device_user_id}
                      </span>
                    ) : <span className="text-zinc-400">—</span>)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {ed ? (
                      <Input type="number" min="0" max="100" step="0.5"
                        value={ed.commission_percentage}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], commission_percentage: e.target.value } }))}
                        className="h-8 rounded-sm text-right text-[12px] font-mono"
                        data-testid={`edit-pct-${r.id}`} />
                    ) : (
                      <span className="font-semibold text-[13px] text-emerald-700">{Number(r.commission_percentage || 0).toFixed(1)}%</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {ed ? (
                      <input type="checkbox" checked={!!ed.active}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...s[r.id], active: e.target.checked } }))} />
                    ) : r.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 rounded-sm text-[10px]">Aktif</Badge>
                    ) : (
                      <Badge className="bg-zinc-100 text-zinc-500 rounded-sm text-[10px]">Nonaktif</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {ed ? (
                      <div className="flex justify-center gap-1">
                        <Button size="icon" onClick={() => saveEdit(r.id)} className="h-7 w-7 rounded-sm bg-emerald-700 hover:bg-emerald-800" data-testid={`save-personel-${r.id}`}>
                          <Save className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => cancelEdit(r.id)} className="h-7 w-7 rounded-sm">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-center gap-1">
                        <Button size="icon" variant="outline" onClick={() => startEdit(r)}
                          className="h-7 w-7 rounded-sm text-emerald-700 hover:bg-emerald-50"
                          data-testid={`edit-personel-${r.id}`} title="Edit">
                          <Edit3 className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => del(r)}
                          className="h-7 w-7 rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`delete-personel-${r.id}`} title="Hapus">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="text-[11px] text-zinc-500 border-l-2 border-emerald-300 pl-3 py-1">
        💡 <b>Cara pakai:</b> 1) Tambah semua personel di sini + set % komisi masing-masing.
        2) Di <b>Rekap Penagihan</b>, filter ke armada tertentu (mis. GA-02) & pilih tanggal, lalu klik <b>Assign Teknisi</b> untuk memilih personel yg bertugas hari itu.
        3) Lihat hasil bulanan di halaman <b>Komisi Teknisi</b>.
      </div>

      {/* v84.70 — CUTI KARYAWAN + PUBLIC HOLIDAY SECTION */}
      <CutiSection />

      {/* v85.9 — LOG ABSENSI (ZKTeco ADMS) — pindahan dari halaman Settings */}
      <AttendanceLogSection />
    </div>
  );
}


// ─── v84.70 Cuti Karyawan Section ─────────────────────────────────────
function CutiSection() {
  const [rows, setRows] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: "", label: "" });
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([api.leavesSummary(), api.listHolidays()]);
      setRows(s.rows || []);
      setHolidays(h.items || []);
    } catch (e) {
      toast.error("Gagal load cuti: " + (e?.message || "error"));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function addHoliday() {
    if (!newHoliday.date) { toast.error("Pilih tanggal dulu"); return; }
    try {
      const r = await api.addHoliday(newHoliday.date, newHoliday.label);
      toast.success(`Public holiday ditambahkan. Bonus otomatis diberikan ke ${r.bonus_applied} karyawan aktif hari itu`);
      setNewHoliday({ date: "", label: "" });
      await load();
    } catch (e) {
      toast.error("Gagal tambah: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function delHoliday(date) {
    if (!window.confirm(`Hapus public holiday ${date}?`)) return;
    try { await api.deleteHoliday(date); await load(); toast.success("Dihapus"); }
    catch (e) { toast.error("Gagal hapus"); }
  }

  async function saveLeave(pid) {
    const ed = editing[pid];
    if (!ed) return;
    try {
      const payload = {};
      if (ed.hire_date) payload.hire_date = ed.hire_date;
      if (ed.anniversary_month) payload.anniversary_month = parseInt(ed.anniversary_month);
      if (ed.leave_balance !== undefined) payload.leave_balance = parseInt(ed.leave_balance || 0);
      if (ed.leave_balance_next_year !== undefined) payload.leave_balance_next_year = parseInt(ed.leave_balance_next_year || 0);
      await api.updateLeaveInfo(pid, payload);
      setEditing(s => { const c = { ...s }; delete c[pid]; return c; });
      toast.success("Data cuti disimpan");
      await load();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    }
  }

  const MONTHS = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
                  "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

  return (
    <div className="mt-6 space-y-4" data-testid="cuti-section">
      <div className="flex items-center gap-2">
        <span className="text-emerald-700 text-lg">🏖️</span>
        <h2 className="text-lg font-bold tracking-tight">Cuti Karyawan</h2>
        <span className="text-[11px] text-zinc-500">
          Base 12 hari/tahun setelah 1 tahun aktif, +1 hari tiap public holiday yg dikerjakan.
        </span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto rounded-sm text-[11px] h-7"
          onClick={async () => {
            try {
              const r = await api.rolloverAnniversary();
              const cnt = r.rolled_over_count || 0;
              if (cnt === 0) toast.info(`Belum ada karyawan yg jatuh anniversary di bulan ${new Date().toLocaleDateString('id-ID',{month:'long'})}`);
              else toast.success(`${cnt} karyawan di-rollover`);
              await load();
            } catch (e) { toast.error("Gagal rollover: " + (e?.message || "error")); }
          }}
          data-testid="btn-rollover"
          title="Trigger rollover cuti manual (juga otomatis jalan tiap 00:00 WITA)"
        >
          🔄 Rollover Sekarang
        </Button>
      </div>

      {/* Public Holiday input */}
      <div className="border border-amber-300 bg-amber-50 rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-2">
          Public Holiday (Tanggal Libur Nasional)
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Tanggal (date picker)</label>
            <Input type="date"
              value={newHoliday.date}
              onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })}
              className="h-8 rounded-sm text-[12px] w-40"
              data-testid="new-holiday-date" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] text-zinc-600 block mb-0.5">Nama Libur (opsional)</label>
            <Input value={newHoliday.label}
              onChange={e => setNewHoliday({ ...newHoliday, label: e.target.value })}
              placeholder="mis. Idul Fitri, HUT RI"
              className="h-8 rounded-sm text-[12px]"
              data-testid="new-holiday-label" />
          </div>
          <Button size="sm" onClick={addHoliday} className="h-8 rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="add-holiday-btn">
            + Tambah
          </Button>
        </div>
        {holidays.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {holidays.map(h => (
              <span key={h.date} className="inline-flex items-center gap-1.5 bg-white border border-amber-300 rounded-sm px-2 py-0.5 text-[11px]"
                    data-testid={`holiday-${h.date}`}>
                <span className="font-mono text-zinc-700">{(() => {
                  const [y, m, d] = h.date.split("-");
                  return `${d}/${m}/${(y || "").slice(-2)}`;
                })()}</span>
                {h.label && <span className="text-zinc-500">· {h.label}</span>}
                <button onClick={() => delHoliday(h.date)} className="text-red-500 hover:text-red-700 ml-1" title="Hapus">×</button>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-zinc-500 italic">Belum ada tanggal libur. Tambahkan supaya sistem otomatis bonus cuti ke teknisi yg aktif hari itu.</div>
        )}
      </div>

      {/* Cuti table */}
      <div className="border border-border rounded-sm bg-white overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-zinc-50 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2">Nama</th>
              <th className="px-3 py-2">Nomor HP</th>
              <th className="px-3 py-2 text-center">Tgl Mulai Kerja</th>
              <th className="px-3 py-2 text-center">Periode Cuti</th>
              <th className="px-3 py-2 text-center">Sisa Cuti Saat Ini</th>
              <th className="px-3 py-2 text-center">Sisa Tahun Berikutnya</th>
              <th className="px-3 py-2 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-4">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-4">Belum ada personel</td></tr>
            )}
            {!loading && rows.map(r => {
              const s = r.leave || {};
              const ed = editing[r.id];
              const anniv = ed?.anniversary_month || s.anniversary_month;
              return (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-zinc-50/60"
                    data-testid={`cuti-row-${r.id}`}>
                  <td className="px-3 py-2 font-semibold">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{r.phone || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {ed ? (
                      <Input type="date" value={ed.hire_date || ""}
                        onChange={e => setEditing(x => ({ ...x, [r.id]: { ...x[r.id], hire_date: e.target.value } }))}
                        className="h-7 rounded-sm text-[11px] w-36 mx-auto" />
                    ) : (
                      <span className="font-mono text-[11px] text-zinc-700">{s.hire_date || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ed ? (
                      <select value={ed.anniversary_month || ""}
                        onChange={e => setEditing(x => ({ ...x, [r.id]: { ...x[r.id], anniversary_month: e.target.value } }))}
                        className="h-7 rounded-sm text-[11px] border border-input px-1">
                        <option value="">-</option>
                        {MONTHS.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                      </select>
                    ) : (
                      anniv ? <span className="text-[11px]">{MONTHS[anniv]}</span> : <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ed ? (
                      <Input type="number" min="0"
                        value={ed.leave_balance ?? ""}
                        onChange={e => setEditing(x => ({ ...x, [r.id]: { ...x[r.id], leave_balance: e.target.value } }))}
                        placeholder="sisa"
                        className="h-7 rounded-sm text-[11px] w-16 mx-auto text-right"
                        title="Sisa hari cuti saat ini" />
                    ) : s.eligible ? (
                      <span className={`inline-block px-2 py-0.5 rounded font-mono font-semibold text-[12px] ${s.current_year_remaining > 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
                        {s.current_year_remaining} hari
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-500 italic">Belum eligible (butuh 1th aktif)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ed ? (
                      <Input type="number" min="0"
                        value={ed.leave_balance_next_year ?? ""}
                        onChange={e => setEditing(x => ({ ...x, [r.id]: { ...x[r.id], leave_balance_next_year: e.target.value } }))}
                        placeholder="sisa"
                        className="h-7 rounded-sm text-[11px] w-16 mx-auto text-right"
                        title="Sisa hari cuti tahun berikutnya" />
                    ) : (
                      <span className={`inline-block px-2 py-0.5 rounded font-mono text-[12px] border ${s.next_year_remaining > 0 ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
                        {s.next_year_remaining} hari
                        {s.next_year_bonus > 0 && (
                          <span className="ml-1 text-[9px] text-emerald-700">(+{s.next_year_bonus})</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ed ? (
                      <div className="flex justify-center gap-1">
                        <Button size="icon" onClick={() => saveLeave(r.id)} className="h-6 w-6 rounded-sm bg-emerald-700 hover:bg-emerald-800" data-testid={`save-cuti-${r.id}`}>
                          <Save className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => setEditing(x => { const c = { ...x }; delete c[r.id]; return c; })} className="h-6 w-6 rounded-sm">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="icon" variant="outline" onClick={() => setEditing(x => ({ ...x, [r.id]: { hire_date: s.hire_date || "", anniversary_month: s.anniversary_month || "", leave_balance: r.leave_balance ?? s.current_year_remaining ?? 0, leave_balance_next_year: r.leave_balance_next_year ?? s.next_year_remaining ?? 0 } }))}
                        className="h-6 w-6 rounded-sm text-emerald-700 hover:bg-emerald-50" data-testid={`edit-cuti-${r.id}`} title="Edit data cuti">
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-zinc-500 border-l-2 border-blue-300 pl-3 py-1">
        📘 <b>Aturan:</b> Base 12 hari/tahun setelah 1 tahun aktif bekerja. Setiap public holiday yg dikerjakan +1 hari otomatis ke tahun berikutnya.
        Status <b>Cuti/Izin</b> di kalender kurangi sisa cuti. Status <b>Sakit/Break</b> tidak mengurangi.
      </div>
    </div>
  );
}


// ─── v85.9 / v85.52 Log Absensi (ZKTeco ADMS) — Matrix bulanan ─────────
// Layout mirip Komisi Teknisi: nama karyawan di header (freeze pane),
// tanggal 1 bulan di kolom kiri (freeze pane). Cell menampilkan Tap time +
// Server receive time saja (tanpa tipe/SN). Tap >= 08:06 WITA → font merah.
function AttendanceLogSection() {
  // Helper bulan WITA default: bulan saat ini.
  const monthWita = () => {
    const d = new Date();
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Asia/Makassar", year: "numeric", month: "2-digit",
    }).formatToParts(d);
    const y = parts.find(p => p.type === "year").value;
    const m = parts.find(p => p.type === "month").value;
    return `${y}-${m}`;
  };
  const [month, setMonth] = useState(monthWita());
  const [data, setData] = useState(null);  // {month, dates, employees, cells}
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.attendanceLogsMatrix(month);
      setData(r);
    } catch (e) {
      toast.error("Gagal muat matrix absensi: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const dates = data?.dates || [];
  const employees = data?.employees || [];
  const cells = data?.cells || {};
  const totalsPerDay = data?.totals_per_day || {};
  // Count total masuk di bulan tsb (sum totals_per_day)
  const totalMasuk = Object.values(totalsPerDay).reduce((a, b) => a + b, 0);

  // Highlight: tap "HH:MM:SS" > "08:05:59" (string compare valid utk HH:MM:SS)
  const isLateTap = (hms) => (hms || "") > "08:05:59";

  return (
    <div className="mt-6 space-y-3" data-testid="attendance-log-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-cyan-700 text-lg">⏱️</span>
          <h2 className="text-lg font-bold tracking-tight">Log Absensi — Matrix Bulanan</h2>
          <span className="text-[11px] text-zinc-500">
            {month} · <b>{totalMasuk.toLocaleString("id-ID")}</b> hari kehadiran · {employees.length} karyawan
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="h-9 rounded-sm text-[12px] font-mono w-[160px]"
            data-testid="attendance-month-picker"
          />
          {month !== monthWita() && (
            <Button variant="outline" size="sm" onClick={() => setMonth(monthWita())}
              className="rounded-sm text-[11px]" data-testid="attendance-this-month-btn">
              Bulan Ini
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-sm">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-zinc-400 border border-zinc-200 rounded-sm bg-white">
          <RefreshCw className="w-6 h-6 animate-spin inline mr-2" /> Memuat matrix…
        </div>
      ) : employees.length === 0 ? (
        <div className="text-[12px] text-zinc-500 text-center py-8 border border-dashed border-border rounded-sm">
          Belum ada karyawan ter-map ke mesin. Set <b>ID Mesin Absensi</b> di tabel karyawan di atas.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm bg-white overflow-auto max-h-[70vh]"
             data-testid="attendance-matrix-wrap">
          <table className="w-full text-[11px] border-collapse" data-testid="attendance-matrix-table">
            <thead className="bg-zinc-50 sticky top-0 z-20">
              <tr>
                <th className="w-20 sticky left-0 bg-zinc-50 z-30 text-left px-2 py-1.5 font-semibold text-zinc-700 border-r border-b border-zinc-200">
                  Tanggal
                </th>
                {employees.map(e => (
                  <th key={e.id}
                      className="min-w-[95px] text-center px-1.5 py-1.5 font-semibold text-zinc-700 border-b border-zinc-200"
                      data-testid={`attendance-col-${e.id}`}>
                    <div className="font-semibold text-zinc-800 text-[11px] truncate" title={e.name}>{e.name}</div>
                    <div className="text-[9px] text-zinc-500 font-mono font-normal">ID {e.device_user_id}</div>
                  </th>
                ))}
                <th className="min-w-[70px] text-center px-1 py-1.5 bg-orange-50 text-[#E65100] font-semibold border-b border-zinc-200 text-[10px]">
                  Hadir/Hari
                </th>
              </tr>
            </thead>
            <tbody>
              {dates.map(d => (
                <tr key={d} data-testid={`attendance-row-${d}`} className="hover:bg-zinc-50">
                  <td className="font-mono text-[11px] sticky left-0 bg-white z-10 border-r border-b border-zinc-200 px-2 py-1"
                      title={d}>
                    {d.slice(8, 10)}/{d.slice(5, 7)}
                  </td>
                  {employees.map(e => {
                    const c = cells[d]?.[e.id];
                    const late = c && isLateTap(c.tap);
                    return (
                      <td key={e.id}
                          className="text-center font-mono text-[10px] border-b border-zinc-100 px-1 py-1"
                          data-testid={`attendance-cell-${d}-${e.id}`}>
                        {c ? (
                          <div className="leading-tight">
                            <div
                              className={late ? "text-rose-600 font-semibold" : "text-zinc-800 font-semibold"}
                              title={late ? "Tap > 08:05 WITA — potensi terlambat" : "Tap time"}
                              data-testid={`attendance-tap-${d}-${e.id}`}
                            >
                              {(c.tap || "").slice(0, 5)}
                            </div>
                            <div className="text-[9px] text-zinc-400" title="Diterima server (WITA)">
                              ← {(c.recv || "").slice(0, 5)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center font-mono text-[11px] font-semibold text-[#E65100] bg-orange-50/50 border-b border-zinc-200">
                    {totalsPerDay[d] || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] text-zinc-500 border-l-2 border-cyan-300 pl-3 py-1">
        💡 Angka pertama di cell = <b>jam tap</b> (merah bila ≥ 08:06 WITA). Angka ke-2 (abu-abu) = jam server menerima log. Hanya first-punch per hari yg ditampilkan. Set <b>ID Mesin Absensi</b> karyawan di tabel atas untuk menampilkan baris mereka.
      </div>
    </div>
  );
}
